import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  SimpleGrid, Text,
  Alert,
  Skeleton,
  Box
} from '@mantine/core';
import {
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { reportsApi } from '../../api/reports';
import { QuarterlyReportCard } from '../reports/QuarterlyReportCard';
import { MilestoneStatusCard } from './MilestoneStatusCard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Milestone, MilestoneStatus, Activity } from '../../types/milestones';
import { milestonesApi } from '../../api/milestones';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../common/SectionHeader';
import { PageHeader } from '../common/PageHeader';

import { useGroupQuery } from '../../hooks/useGroupQuery';
import { EmptyReportsState } from '../reports/EmptyReportsState';
import { showApiErrorNotification } from '../../utils/notifications';
import { DynamicDashboard } from './DynamicDashboard';

dayjs.extend(quarterOfYear);

// export const generateQuarterlyReports = (): QuarterlyReport[] => {
//   const reports: QuarterlyReport[] = [];
//   const startYear = 2024;
//   const currentDate = dayjs();

//   for (let year = startYear; year < startYear + 10; year++) {
//     for (let quarter = 1; quarter <= 4; quarter++) {
//       const dueDate = dayjs(`${year}-${quarter * 3}-15`);
//       const isPast = dueDate.isBefore(currentDate);
      
//       // Default status logic
//       let status: QuarterlyReport['status'] = 'not_submitted';
//       if (isPast) {
//         status = Math.random() > 0.3 ? 'approved' : 
//                 Math.random() > 0.5 ? 'needs_attention' : 'pending';
//       }

//       // Override status for Q3 and Q4 2024
//       if (year === 2024) {
//         if (quarter === 3) {
//           status = 'not_submitted'; // This will show as overdue since it's in the past
//         } else if (quarter === 4) {
//           status = 'needs_attention';
//         }
//       }

//       reports.push({
//         id: `${year}-Q${quarter}`,
//         year,
//         quarter: quarter as 1 | 2 | 3 | 4,
//         dueDate: dueDate.format('YYYY-MM-DD'),
//         status,
//         submissionDate: status !== 'not_submitted' ? 
//           dueDate.subtract(Math.floor(Math.random() * 14), 'day').format('YYYY-MM-DD') : 
//           undefined
//       });
//     }
//   }
//   return reports;
// };

// Helper function to filter reports
// const getFilteredReports = (reports: QuarterlyReport[], currentDate: dayjs.Dayjs): QuarterlyReport[] => {
//   try {
//     if (!reports || !Array.isArray(reports) || reports.length === 0) {
//       console.warn('No reports data available or invalid reports data:', reports);
//       return [];
//     }
    
//     console.log(`Filtering ${reports.length} reports`, reports);
    
//     // Sort reports by year and quarter
//     const filteredReports = reports.sort((a, b) => {
//       if (a.year !== b.year) return a.year - b.year;
//       return a.quarter - b.quarter;
//     });

//     // Find the next upcoming report
//     const upcomingReport = filteredReports
//       .find(report => report.dueDate && dayjs(report.dueDate).isAfter(currentDate));

//     // Get the 3 most recent past reports
//     const pastReports = filteredReports
//       .filter(report => report.dueDate && dayjs(report.dueDate).isBefore(currentDate))
//       .slice(-3);

//     const result = [...pastReports, ...(upcomingReport ? [upcomingReport] : [])]
//       .filter((report): report is QuarterlyReport => report !== undefined);
    
//     console.log(`Filtered to ${result.length} reports to display`);
    
//     return result;
//   } catch (error) {
//     console.error('Error in getFilteredReports:', error);
//     return [];
//   }
// };

export function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Separate queries for different data
  // Using useGroupQuery to include selectedGroup in the query keys

  const {
    data: reports,
    isLoading: isReportsLoading,
    error: reportsError
  } = useGroupQuery(
    ['recent-reports'],
    reportsApi.getQuarterlyReports,
  );



  const {
    data: upcomingMilestonesData,
    isLoading: isMilestonesLoading,
    error: milestonesError
  } = useGroupQuery(
    ['upcomingMilestones'],
    milestonesApi.getUpcomingMilestones,
  );

  // Full milestones data for getting activity context
  const {
    data: fullMilestonesData,
    error: fullMilestonesError
  } = useGroupQuery(
    ['milestones'],
    milestonesApi.getMilestones,
  );

  // Milestone status update mutation
  const updateMilestoneStatusMutation = useMutation({
    mutationFn: ({ milestoneId, status, context }: { 
      milestoneId: string; 
      status: MilestoneStatus; 
      context: any 
    }) => milestonesApi.updateMilestoneStatus(milestoneId, status, context),
    onSuccess: () => {
      // Invalidate both milestone queries
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
      queryClient.invalidateQueries({ queryKey: ['upcomingMilestones'] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to update milestone status');
    }
  });

  // State for upcoming milestones
  const [milestonesToShow, setMilestonesToShow] = useState<Milestone[]>([]);
  
  // Get all activities from the full milestones data
  const allActivities = useMemo(() => {
    if (!fullMilestonesData?.objectives) return [];
    
    const activities: Activity[] = [];
    fullMilestonesData.objectives.forEach(obj => {
      obj.key_activities.forEach(ka => {
        ka.goals.forEach(goal => {
          activities.push(...goal.activities);
        });
      });
    });
    
    return activities;
  }, [fullMilestonesData]);
  
  // We no longer use a combined loading state
  // Instead, individual sections will handle their own loading states

  // Handle API errors with notifications and console logging

  useEffect(() => {
    if (reportsError) {
      console.error("Error fetching reports:", reportsError);
      showApiErrorNotification(reportsError, 'Failed to load recent reports. Please try again later.');
    }
  }, [reportsError]);



  useEffect(() => {
    if (milestonesError) {
      console.error("Error fetching milestones:", milestonesError);
      showApiErrorNotification(milestonesError, 'Failed to load upcoming milestones. Please try again later.');
    }
  }, [milestonesError]);

  useEffect(() => {
    if (fullMilestonesError) {
      console.error("Error fetching full milestones:", fullMilestonesError);
      showApiErrorNotification(fullMilestonesError, 'Failed to load milestone details. Please try again later.');
    }
  }, [fullMilestonesError]);
  
  // Update milestones when data changes
  useEffect(() => {
    if (upcomingMilestonesData) {
      // The API returns upcoming milestones array, take the first 4
      const milestones = Array.isArray(upcomingMilestonesData) ? upcomingMilestonesData : [];
      const milestonesToDisplay = milestones.slice(0, 4);
      setMilestonesToShow(milestonesToDisplay);
    } else {
      setMilestonesToShow([]);
    }
  }, [upcomingMilestonesData]);

  // Filter reports to show only relevant ones
  // const currentDate = dayjs();
  const reportsToShow = reports ? [...reports].sort((a, b) => 
    dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf()
  ) : [];
  
  // Function to get activity title for a milestone
  const getActivityTitle = (milestone: Milestone): string => {
    // For upcoming milestones from API, we might not have the full nested structure
    // so we can't easily get the activity title. Return a generic title or empty string.
    if (milestone.activity_id && !allActivities.length) {
      return `Activity ${milestone.activity_id}`;
    }
    
    const activity = allActivities.find(a => a.milestones.some(m => m.id === milestone.id));
    return activity ? activity.title : '';
  };

  const handleSubmit = async (reportId: string) => {
    try {

      navigate(`/reports/${reportId}/submit`);
    } catch (error) {
      notifications.hide('loading-report');
      notifications.show({
        title: 'Error',
        message: 'Failed to load report. Please try again.',
        color: 'red',
      });
      console.error('Error loading report:', error);
    }
  };

  const handleView = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleUpdate = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleViewAll = (): void => {
    navigate('/reports');
  };

  const handleViewAllMilestones = (): void => {
    navigate('/milestones');
  };

  // Handle milestone toggle - useGroupQuery will automatically refetch when needed
  const handleToggleMilestone = async (compositeId: string, status: MilestoneStatus): Promise<void> => {
    // Parse the composite ID: "milestoneId:activityId:goalId:keyActivityId:objectiveId"
    const [milestoneId, activityId, goalId, keyActivityId, objectiveId] = compositeId.split(':');
    
    await updateMilestoneStatusMutation.mutateAsync({
      milestoneId,
      status,
      context: {
        objective_id: objectiveId,
        key_activity_id: keyActivityId,
        goal_id: goalId,
        activity_id: activityId
      }
    });
  };

  return (
    <Container size="xl" mt="xl" pb="xl" role="main" aria-label="Dashboard" px={0}>
      <PageHeader title="Dashboard" subtitle="Monitor report submissions, track performance metrics, and manage quarterly reporting requirements." />

      {/* Open Reports Section */}
      <Box mb={60}>
        <SectionHeader
          title="Open Reports"
          action={{
            label: 'View All',
            onClick: handleViewAll,
            ariaLabel: 'View all reports',
          }}
        />

        {reportsError ? (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="xl" role="alert">
            Failed to load reports. Please try again later.
          </Alert>
        ) : null}

        <SimpleGrid
          cols={4}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1400, cols: 3 },
            { maxWidth: 1024, cols: 2 },
            { maxWidth: 768, cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' },
          ]}
        >
          {isReportsLoading ? (
            Array(4)
              .fill(0)
              .map((_, index) => <Skeleton key={index} height={180} radius="md" animate={true} />)
          ) : reportsError ? null : reportsToShow.length > 0 ? (
            reportsToShow.map((report) => <QuarterlyReportCard key={report.id} report={report} onSubmit={handleSubmit} onView={handleView} onUpdate={handleUpdate} />)
          ) : (
            <Box style={{ gridColumn: '1 / -1' }}>
              <EmptyReportsState />
            </Box>
          )}
        </SimpleGrid>
      </Box>

      {/* Upcoming Milestones Section */}
      <Box mb={60}>
        <SectionHeader
          title="Upcoming Milestones"
          action={{
            label: 'View All',
            onClick: handleViewAllMilestones,
            ariaLabel: 'View all milestones',
          }}
        />

        {milestonesError ? (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="xl" role="alert">
            Failed to load upcoming milestones. Please try again later.
          </Alert>
        ) : null}

        <SimpleGrid
          cols={4}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1400, cols: 3 },
            { maxWidth: 1024, cols: 2 },
            { maxWidth: 768, cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' },
          ]}
        >
          {isMilestonesLoading ? (
            Array(4)
              .fill(0)
              .map((_, index) => <Skeleton key={index} height={180} radius="md" animate={true} />)
          ) : milestonesError ? null : milestonesToShow.length > 0 ? (
            milestonesToShow.map((milestone) => (
              <MilestoneStatusCard
                key={`${milestone.objective_id}:${milestone.key_activity_id}:${milestone.goal_id}:${milestone.activity_id}:${milestone.id}`}
                milestone={milestone}
                activityTitle={getActivityTitle(milestone)}
                onToggle={handleToggleMilestone}
              />
            ))
          ) : (
            <Text color="dimmed" style={{ gridColumn: '1 / -1' }}>
              No upcoming milestones found. View all milestones to see your complete list.
            </Text>
          )}
        </SimpleGrid>
      </Box>

      {/* Performance Dashboard Section */}
      <Box mb={60}>
        <DynamicDashboard />
      </Box>
    </Container>
  );
}
